/**
 * <s-product-image-zoom> — PDP image zoom preloader.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    // Bring the selected variant's image to the front, on load and on every variant change.
    document.addEventListener('variant:media-change', this.#handleVariantMedia, opts);
    this.#showMedia(this.dataset.selectedMediaId, false);
  }

  #handleVariantMedia = (event) => {
    const { productId, mediaId } = event.detail || {};
    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;
    this.#filterByColor(event.target);
    this.#showMedia(mediaId, true);
  };

  // Shows only the photos matched to the selected variant's color (unmatched photos always show).
  // Liquid writes data-variant-colors (variant id → color handle) and data-media-color per photo.
  #filterByColor(source) {
    const raw = this.dataset.variantColors;
    if (!raw) return;

    let colors;
    try { colors = JSON.parse(raw); } catch { return; }

    const form = (source instanceof Element && source.closest('[data-product-form]'))
      || (source instanceof Element && source.querySelector?.('[data-product-form]'))
      || null;
    const variantInput = (form || this.parentElement)?.querySelector('input[name="id"]');
    const color = variantInput instanceof HTMLInputElement ? colors[variantInput.value] : undefined;

    const items = Array.from(this.querySelectorAll('[data-product-gallery] > [data-media-id]'));
    const hasMatch = Boolean(color) && items.some((item) => item.dataset.mediaColor === color);

    let visibleCount = 0;
    for (const item of items) {
      const itemColor = item.dataset.mediaColor;
      item.hidden = hasMatch && Boolean(itemColor) && itemColor !== color;
      if (!item.hidden) visibleCount += 1;
    }

    // Mirrors the Liquid rule: two columns on desktop only when more than two photos show.
    this.querySelector('[data-product-gallery]')?.classList.toggle('md:grid-cols-2', visibleCount > 2);
  }

  #showMedia(mediaId, userInitiated) {
    if (!mediaId) return;

    const gallery = this.querySelector('[data-product-gallery]');
    const item = gallery?.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!gallery || !item) return;

    if (gallery.firstElementChild !== item) gallery.prepend(item);
    item.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = userInitiated && !reduceMotion ? 'smooth' : 'auto';
    gallery.scrollTo({ left: 0, behavior });

    if (userInitiated && gallery.getBoundingClientRect().top < 0) {
      gallery.scrollIntoView({ block: 'start', behavior });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
